import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@ui/Form";
import { Input } from "@ui/Input";
import { useMultiStepForm } from "@/hooks";
import { cn } from "@/utils/tailwind";
import type { StepProps } from "../StepProps";
import Consents from "./Consents";

export const Step5: FC<StepProps> = ({ isSummary }) => {
  const { t } = useTranslation();
  const { form, validateStepAndRun } = useMultiStepForm();

  const isError = (key: keyof NonNullable<typeof form.formState.errors.step5>) =>
    Boolean(form.formState.errors.step5?.[key]);

  const fields = [
    { name: "companyName", label: t("step5.q1_companyName") },
    { name: "email", label: t("step5.q1_email") },
    { name: "firstName", label: t("step5.q1_firstName") },
    { name: "phone", label: t("step5.q1_phoneNumber") },
    { name: "lastName", label: t("step5.q1_lastName") },
  ] as const;

  return (
    <div className="space-y-4" data-test="form-section">
      <p className="text-base font-bold">{t(`${isSummary ? "summary" : "step5"}.q1`)}</p>

      <div
        className={cn(
          "grid grid-cols-1 gap-x-8 gap-y-3 px-4 lg:grid-cols-2",
          isSummary && "px-0 md:px-4",
        )}
        data-test="form-grid"
      >
        {fields.map(({ name, label }) => (
          <FormField
            key={name}
            control={form.control}
            name={`step5.${name}`}
            render={({ field }) => (
              <FormItem className="gap-1!">
                <FormControl>
                  <div className="space-y-0.5">
                    <FormDescription>{label}</FormDescription>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      onBlur={() => {
                        field.onBlur();
                        if (isSummary) {
                          void validateStepAndRun("step5", undefined, {
                            skipCalculations: true,
                          });
                        }
                      }}
                      aria-invalid={isError(name)}
                      dataTest={`${name}-input`}
                    />
                  </div>
                </FormControl>
                <FormMessage data-test={`${name}-error`} />
              </FormItem>
            )}
          />
        ))}
      </div>
      {!isSummary && <Consents />}
    </div>
  );
};
